import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useHistory, useParams } from "react-router-dom";
import { withTranslation } from 'react-i18next';
import { ProgressBar } from 'primereact/progressbar';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { Badge } from 'primereact/badge';
import { Chart } from 'primereact/chart';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import axios from "axios";

const DashboardTemplateShowComponent = (props) => {
  const { t } = props;
  const history = useHistory();
  const { id } = useParams();

  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [chartsReady, setChartsReady] = useState(false);
  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);

  const apiCall = useCallback((method, url, data) => {
    return axios({
      method,
      url: process.env.REACT_APP_API_HOST + url,
      data,
      headers: {
        'Authorization': 'Bearer ' + localStorage.getItem("userToken"),
        'App-Network': localStorage.getItem("networkId")
      }
    });
  }, []);

  const colorCache = useMemo(() => new Map(), []);
  const generateColor = useCallback((key) => {
    if (colorCache.has(key)) return colorCache.get(key);
    const c = '#' + Math.floor(Math.random() * 16777215).toString(16).padStart(6, '0');
    colorCache.set(key, c);
    return c;
  }, [colorCache]);

  const measureContainer = useCallback(() => {
    const w = containerRef.current?.clientWidth ?? 0;
    setContainerWidth(w);
    return w;
  }, []);

  useEffect(() => {
    setLoading(true);
    apiCall('get', `/api/v2/dashboard-template/${id}`)
      .then((response) => {
        setTemplate(response.data.data);
        setChartsReady(false);

        const tryReady = () => {
          const w = measureContainer();
          if (w > 300) {
            setChartsReady(true);
            return;
          }
          requestAnimationFrame(tryReady);
        };
        requestAnimationFrame(tryReady);
      })
      .catch((error) => {
        window.App.toastShow('error', t('error'), error.toString(), '', error.response);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [id, apiCall, t, measureContainer]);

  useEffect(() => {
    const onResize = () => {
      measureContainer();
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [measureContainer]);

  const getLgColClass = (count) => {
    if (count === 1) return 'p-col-12';
    if (count === 2) return 'p-col-12 p-lg-6';
    if (count === 3) return 'p-col-12 p-lg-4';
    if (count === 4) return 'p-col-12 p-lg-3';
    return 'p-col-12 p-lg-2';
  };

  const getBindingValue = (node, key) => {
    if (!node?.bindings) return '';
    return node.bindings[key] || '';
  };

  const ChartLoader = ({ height = 300 }) => (
    <div className="p-d-flex p-ai-center p-jc-center" style={{ height }}>
      <i className="pi pi-spin pi-spinner" style={{ fontSize: '1.5rem', opacity: 0.6 }} />
    </div>
  );

  const renderKpi = (node, key) => {
    const theme = node.subtitle_theme;
    const arrowIcon =
      theme === 'success' ? 'pi pi-arrow-up' :
      theme === 'danger' ? 'pi pi-arrow-down' :
      theme === 'warning' ? 'pi pi-exclamation-triangle' : null;

    return (
      <div key={key} className="p-my-1">
        <Card className="p-p-2 p-shadow-1" style={{ borderRadius: 12 }}>
          <div className="p-d-flex p-jc-between p-ai-start">
            <div>
              <small className="p-text-uppercase p-text-secondary" style={{ letterSpacing: 0.6 }}>
                {node.title || 'KPI'}
              </small>
              <div className="p-mt-1" style={{ fontSize: 22, fontWeight: 700 }}>
                {getBindingValue(node, 'amount') || 'kpi.amount'}
              </div>
            </div>
            <div className="p-ml-2" style={{ opacity: 0.85 }}>
              <i className={`pi pi-${node.icon || 'info-circle'}`} style={{ fontSize: '1.6em' }} />
            </div>
          </div>

          <div className="p-mt-2">
            <span className={"text-" + (theme || '')}>
              <span className="p-text-bold">
                {arrowIcon && <i className={arrowIcon}></i>}
                {arrowIcon && <>&nbsp;</>}
                {getBindingValue(node, 'subtitle') || 'kpi.subtitle'}
              </span>
              {node.description ? <> {node.description}</> : null}
            </span>
          </div>

          <div className="p-mt-3">
            <small className="p-text-secondary">
              Binding amount: <strong>{getBindingValue(node, 'amount') || '—'}</strong>
            </small>
            <br />
            <small className="p-text-secondary">
              Binding subtitle: <strong>{getBindingValue(node, 'subtitle') || '—'}</strong>
            </small>
          </div>
        </Card>
      </div>
    );
  };

  const buildPreviewChartData = (node) => {
    const binding = getBindingValue(node, 'data') || 'chart.data';

    if (node.type === 'pie') {
      return {
        labels: ['A', 'B', 'C'],
        datasets: [
          {
            data: [35, 40, 25],
            backgroundColor: [
              generateColor(binding + '-1'),
              generateColor(binding + '-2'),
              generateColor(binding + '-3')
            ]
          }
        ]
      };
    }

    return {
      labels: ['1', '2', '3'],
      datasets: [
        {
          label: binding,
          backgroundColor: generateColor(binding),
          data: [120, 90, 150]
        }
      ]
    };
  };

  const renderBarOrPie = (node, key) => {
    const chartType = node.type === 'pie' ? 'pie' : 'bar';
    const data = buildPreviewChartData(node);

    return (
      <div key={key} className="p-my-1">
        <Card className="p-p-2 p-shadow-1" style={{ borderRadius: 12 }}>
          <div className="p-d-flex p-jc-between p-ai-center p-mb-2">
            <div>
              <small className="p-text-uppercase p-text-secondary" style={{ letterSpacing: 0.6 }}>
                {node.title || 'Chart'}
              </small>
            </div>
            {node.icon && (
              <i className={`pi pi-${node.icon}`} style={{ fontSize: '1.2em', opacity: 0.75 }} />
            )}
          </div>

          <div className="p-mb-2">
            <small className="p-text-secondary">
              Binding: <strong>{getBindingValue(node, 'data') || '—'}</strong>
            </small>
            {node.unit ? (
              <>
                <br />
                <small className="p-text-secondary">
                  {t('dashboardTemplateUnit')}: <strong>{node.unit}</strong>
                </small>
              </>
            ) : null}
          </div>

          <div style={{ height: 300 }}>
            {(chartsReady && containerWidth > 300) ? (
              <Chart
                type={chartType}
                data={data}
                style={{ width: '100%', height: '300px' }}
                options={{
                  maintainAspectRatio: false,
                  indexAxis: chartType === 'bar' ? 'y' : undefined,
                  plugins: {
                    legend: { labels: { color: '#000' } }
                  },
                  scales: chartType === 'bar' ? {
                    x: { ticks: { color: '#444' }, grid: { color: '#f3f3f3' } },
                    y: { ticks: { color: '#444' }, grid: { color: '#f3f3f3' } }
                  } : undefined
                }}
              />
            ) : (
              <ChartLoader height={300} />
            )}
          </div>
        </Card>
      </div>
    );
  };

  const renderTable = (node, key) => {
    const binding = getBindingValue(node, 'data') || 'table.data';

    const previewRows = [
      { col1: `${t('dashboardTemplateSample')} 1`, col2: '123', col3: t('active') },
      { col1: `${t('dashboardTemplateSample')} 2`, col2: '456', col3: t('active') },
      { col1: `${t('dashboardTemplateSample')} 3`, col2: '789', col3: t('inactive') }
    ];

    return (
      <div key={key} className="p-my-1">
        <Card className="p-p-2 p-shadow-1" style={{ borderRadius: 12 }}>
          <div className="p-d-flex p-jc-between p-ai-center p-mb-3">
            <small className="p-text-uppercase p-text-secondary" style={{ letterSpacing: 0.6 }}>
              {node.title || t('dashboardTemplateTable')}
            </small>
            {node.icon && <i className={`pi pi-${node.icon}`} style={{ opacity: 0.75 }} />}
          </div>

          <div className="p-mb-3">
            <small className="p-text-secondary">
              Binding: <strong>{binding}</strong>
            </small>
          </div>

          <DataTable value={previewRows} responsiveLayout="scroll" className="p-datatable-sm" emptyMessage={t('noResults')}>
            <Column field="col1" header={t('dashboardTemplateColumn1')} />
            <Column field="col2" header={t('dashboardTemplateColumn2')} />
            <Column field="col3" header={t('dashboardTemplateColumn3')} />
          </DataTable>
        </Card>
      </div>
    );
  };

  const renderUnknown = (node, key) => {
    return (
      <div key={key} className="p-my-1">
        <Card className="p-p-2 p-shadow-1" style={{ borderRadius: 12 }}>
          <div className="p-d-flex p-jc-between p-ai-center p-mb-2">
            <small className="p-text-uppercase p-text-secondary" style={{ letterSpacing: 0.6 }}>
              {node.title || t('dashboardTemplateUnknownElement')}
            </small>
            <Badge value={node.type || 'unknown'} severity="warning" />
          </div>

          <div className="p-text-secondary">
            {t('dashboardTemplateUnknownElementNotRendered')}
          </div>
        </Card>
      </div>
    );
  };

  const renderVisualization = (node, key) => {
    switch (node?.type) {
      case 'kpi':
        return renderKpi(node, key);
      case 'bar':
      case 'pie':
        return renderBarOrPie(node, key);
      case 'table':
        return renderTable(node, key);
      default:
        return renderUnknown(node, key);
    }
  };

  const renderNode = (node, key = 'root') => {
    if (!node) return null;

    if (node.type === 'verticalLayout') {
      return (
        <div key={key} className="p-d-flex p-flex-column">
          {node.elements?.map((child, i) => renderNode(child, `${key}-v-${i}`))}
        </div>
      );
    }

    if (node.type === 'horizontalLayout') {
      const count = node.elements?.length ?? 0;
      const colClass = getLgColClass(count);

      return (
        <div key={key} className="p-grid">
          {node.elements?.map((child, i) => (
            <div key={`${key}-hcol-${i}`} className={colClass}>
              {renderNode(child, `${key}-h-${i}`)}
            </div>
          ))}
        </div>
      );
    }

    return renderVisualization(node, key);
  };

  return (
    <div ref={containerRef} className="project-dashboard widget" style={{ position: 'relative' }}>
      {loading && (
        <ProgressBar
          mode="indeterminate"
          style={{ height: '5px', position: 'absolute', top: 0, left: 0, right: 0, borderRadius: 0 }}
        />
      )}

      {template?.name && (
        <div className="p-grid p-ai-center p-mb-2">
          <div className="p-col-12 p-lg-6">
            <h2 className="p-m-0 p-p-0 text-primary" style={{ fontWeight: 800 }}>
              {template.name}
            </h2>
            <small className="p-text-secondary">
              {t('dashboardTemplateCode')}: <strong>{template.code}</strong>
              {template?.version ? <> | {t('dashboardTemplateVersion')}: <Badge value={template.version} className="p-badge-info" /></> : null}
              <> | </>
              {template?.is_active ? (
                <Badge value={t('active')} className="p-badge-success" />
              ) : (
                <Badge value={t('inactive')} className="p-badge-danger" />
              )}
            </small>
          </div>

          <div className="p-col-12 p-lg-6 p-text-right">
            <Button
              label={t('back')}
              icon="pi pi-arrow-left"
              className="p-button-text p-mr-2"
              onClick={() => history.push('/settings/dashboard-template')}
            />
            <Button
              label={t('editAction')}
              icon="pi pi-pencil"
              className="p-button-warning"
              onClick={() => history.push(`/settings/dashboard-template/update/${template.id}`)}
            />
          </div>
        </div>
      )}

      {template?.layout ? (
        <div className="p-mt-1">
          {renderNode(template.layout, 'template-root')}
        </div>
      ) : (
        !loading && <small className="p-text-secondary">{t('noData')}</small>
      )}
    </div>
  );
};

export default withTranslation('common')(DashboardTemplateShowComponent);
